import { z } from 'zod/mini';
import { OrderLineGroupSchema, OrderLineRuleSchema, OrderLineSchema } from '~/validation/api/packageSessionResponse';
import Prerequisite from './rulePrerequisite';
import AlwaysTogether from './ruleAlwaysTogether';
import Recommendation from './ruleRecommendation';
import MutuallyExclusive from './ruleMutuallyExclusive';
import DoNotShow from './ruleDoNotShow';
import PerPax from './rulePerPax';

export const applyRulesToOrderLines = (
  orderLines: z.infer<typeof OrderLineSchema>[],
  rules: z.infer<typeof OrderLineRuleSchema>[],
  pax: number,
): z.infer<typeof OrderLineSchema>[] => {
  let clonedOrderLines = [...orderLines];

  rules.forEach((rule) => {
    const sourceOrderLines = clonedOrderLines.filter(
      (currentSourceOrderLine) => rule.source_order_lines.includes(currentSourceOrderLine.id),
    );

    const targetOrderLines = clonedOrderLines.filter(
      (currentTargetOrderLine) => rule.target_order_lines.includes(currentTargetOrderLine.id),
    );

    if (rule.rule_type === 'do_not_show') {
      clonedOrderLines = (new DoNotShow()).apply(
        clonedOrderLines,
        sourceOrderLines,
        targetOrderLines,
        rule,
        pax,
      );
    } else if (rule.rule_type === 'prerequisite') {
      clonedOrderLines = (new Prerequisite()).apply(
        clonedOrderLines,
        sourceOrderLines,
        targetOrderLines,
        rule,
        pax,
      );
    } else if (rule.rule_type === 'always_together') {
      clonedOrderLines = (new AlwaysTogether()).apply(
        clonedOrderLines,
        sourceOrderLines,
        targetOrderLines,
        rule,
        pax,
      );
    } else if (rule.rule_type === 'recommendation') {
      clonedOrderLines = (new Recommendation()).apply(
        clonedOrderLines,
        sourceOrderLines,
        targetOrderLines,
        rule,
        pax,
      );
    }
  });

  return clonedOrderLines;
};

export const applyGroupsToOrderLines = (
  orderLines: z.infer<typeof OrderLineSchema>[],
  defaultOrderLines: z.infer<typeof OrderLineSchema>[],
  groups: z.infer<typeof OrderLineGroupSchema>[],
  pax: number,
): {
  orderLines: z.infer<typeof OrderLineSchema>[],
  errors: string[],
} => {
  let orderLinesClone = [...orderLines];
  const errors: string[] = [];

  // We should first apply the additions per pax rules, because the other
  // groups depend on them. For example, mutually exclusive should
  // behave differently if the orderLines are per pax or not.
  groups.forEach((group) => {
    if (group.type === 'additions_per_pax') {
      const perPax = new PerPax();
      orderLinesClone = perPax.apply(orderLinesClone, defaultOrderLines, group, pax);
      errors.push(...perPax.errors(orderLinesClone, defaultOrderLines, group, pax));
    }
  });

  groups.forEach((group) => {
    if (group.type === 'mutually_exclusive' || group.type === 'mutually_exclusive_mandatory') {
      const mutuallyExclusive = new MutuallyExclusive();
      orderLinesClone = mutuallyExclusive.apply(orderLinesClone, defaultOrderLines, group, pax);
      errors.push(...mutuallyExclusive.errors(orderLinesClone, defaultOrderLines, group, pax));
    }
  });

  return {
    orderLines: orderLinesClone,
    errors,
  };
};

export const applyRules = (
  orderLines: z.infer<typeof OrderLineSchema>[],
  defaultOrderLines: z.infer<typeof OrderLineSchema>[],
  rules: z.infer<typeof OrderLineRuleSchema>[],
  groups: z.infer<typeof OrderLineGroupSchema>[],
  pax: number,
) => {
  let orderLinesClone = [...orderLines];
  const errors: string[] = [];

  // If the session has order line rules, apply them. Order line rules are for example
  // that one option is only available when another option is selected, etc.
  if (rules) {
    orderLinesClone = applyRulesToOrderLines(
      orderLinesClone,
      rules,
      pax,
    );
  }

  // If the session has order line groups, apply them. Order line groups are for example
  // that one option is only available when another option is selected, etc.
  if (groups) {
    const { orderLines: groupedOrderLines, errors: groupErrors } = applyGroupsToOrderLines(
      orderLinesClone,
      defaultOrderLines,
      groups,
      pax,
    );
    orderLinesClone = groupedOrderLines;

    errors.push(...groupErrors);
  }

  return {
    orderLines: orderLinesClone,
    errors,
  };
};
